// Shelter Stone signs one or more prepared contracts, and each counterparty is
// then emailed. One contract shows its documents to read first; several (a bulk
// send of engagement schedules) are listed, each viewable, and the one signature
// is applied to each in turn — one request per contract, so nothing runs for
// minutes, with a result per person at the end.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  LinearProgress, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PdfPreview from "./PdfPreview";
import SignaturePad from "./SignaturePad";
import { ContractDocumentDialog, fetchContractLink } from "./ContractSummary";
import { CONTRACTS_API } from "../contracts";
import { useContractConfig } from "../useContractConfig";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { notify } from "../notify";

const errorText = (err) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : "Couldn't be sent.";
};

/**
 * Props: open, contracts (drafts, ContractRead[]), recipientLabel ("organiser" |
 * "person"), notes (strings to show above — e.g. who couldn't be prepared),
 * onClose, onDone(updatedContracts).
 */
export default function ShelterStoneSignDialog({ open, contracts, recipientLabel = "recipient",
                                                 notes = [], onClose, onDone }) {
  const { me } = useMe();
  const config = useContractConfig();
  const fullScreen = useFullScreenDialog();
  const [tab, setTab] = useState(0);
  const [viewing, setViewing] = useState(null);   // {contract, doc}
  const [progress, setProgress] = useState(null); // {done, total}
  const [results, setResults] = useState(null);   // [{contract, ok, error}]

  useEffect(() => {
    if (open) { setTab(0); setResults(null); setProgress(null); }
  }, [open]);

  const list = contracts || [];
  const single = list.length === 1 ? list[0] : null;
  const busy = progress !== null && results === null;

  const sign = async (signature) => {
    const out = [];
    setProgress({ done: 0, total: list.length });
    for (const c of list) {
      const hashes = Object.fromEntries(c.documents.map(d => [String(d.id), d.current_sha256]));
      try {
        const res = await axios.post(`${CONTRACTS_API}/${c.id}/sign`, { ...signature, hashes },
                                     { suppressErrorToast: list.length > 1 });
        out.push({ contract: res.data, ok: true });
      } catch (err) {
        out.push({ contract: c, ok: false, error: errorText(err) });
        if (list.length === 1) { setProgress(null); return; }  // the toast said why
      }
      setProgress({ done: out.length, total: list.length });
    }
    setResults(out);
    const sent = out.filter(r => r.ok).length;
    notify(sent === out.length
      ? `Signed and sent${out.length > 1 ? ` to ${sent} people` : ""}.`
      : `Sent ${sent} of ${out.length} — see the list for the rest.`, sent ? "success" : "error");
    onDone?.(out.filter(r => r.ok).map(r => r.contract));
    if (list.length === 1 && out[0]?.ok) onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        {single ? `Sign and send — ${single.signer_name || ""}` : `Sign and send ${list.length} contracts`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {config && !config.mail_configured && (
            <Alert severity="warning">
              Email isn't configured on this server, so nothing will actually be sent — the
              messages are written to the server's outbox instead.
            </Alert>
          )}

          {notes.length > 0 && (
            <Alert severity="warning">{notes.map((n, i) => <div key={i}>{n}</div>)}</Alert>
          )}
          {single?.blank_fields?.length > 0 && (
            <Alert severity="warning">Left blank (nothing on the record): {single.blank_fields.join(", ")}.</Alert>
          )}

          {single && single.documents.length > 0 && (
            <Box>
              {single.documents.length > 1 && (
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
                  {single.documents.map(d => <Tab key={d.id} label={d.title} />)}
                </Tabs>
              )}
              {single.documents[tab] && (
                <PdfPreview title={single.documents[tab].title}
                            reloadKey={`${single.id}-${single.documents[tab].id}-${single.documents[tab].current_sha256}`}
                            load={() => fetchContractLink(single, single.documents[tab], "current")} height={440} />
              )}
            </Box>
          )}

          {!single && (
            <Stack spacing={0.5}>
              {list.map(c => {
                const r = results?.find(x => x.contract.id === c.id);
                return (
                  <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {r && (r.ok ? <CheckCircleIcon color="success" fontSize="small" />
                                : <ErrorIcon color="error" fontSize="small" />)}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {c.signer_name}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {c.signer_email ? ` — ${c.signer_email}` : ""}
                        {r && !r.ok ? ` — ${r.error}` : ""}
                      </Typography>
                    </Typography>
                    {c.documents[0] && (
                      <Button size="small" startIcon={<PictureAsPdfIcon />}
                              onClick={() => setViewing({ contract: c, doc: c.documents[0] })}>View</Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}

          <Divider />
          {results ? (
            <Alert severity={results.every(r => r.ok) ? "success" : "warning"}>
              {results.filter(r => r.ok).length} of {results.length} signed and sent.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                You are signing on behalf of Shelter Stone. The {recipientLabel}
                {list.length > 1 ? "s are" : " is"} then emailed to sign
                {single?.kind === "client" ? " using a private link" : " in the app"}.
              </Typography>
              {busy && (
                <Box>
                  <LinearProgress variant="determinate"
                                  value={(100 * progress.done) / Math.max(progress.total, 1)} />
                  <Typography variant="caption" color="text.secondary">
                    {progress.done} of {progress.total} done…
                  </Typography>
                </Box>
              )}
              <SignaturePad defaultName={me?.name || ""} busy={busy}
                            consentText={config?.consent?.shelter_stone || "I agree to sign electronically."}
                            submitLabel={list.length > 1 ? `I Agree and Sign (${list.length})` : undefined}
                            onSign={sign} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{results ? "Close" : "Cancel"}</Button>
      </DialogActions>
      {viewing && (
        <ContractDocumentDialog contract={viewing.contract} doc={viewing.doc}
                                onClose={() => setViewing(null)} />
      )}
    </Dialog>
  );
}
